const moment = require("moment-jalaali");

function normalizeAmountDigits(value) {
  return String(value)
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 1776))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 1632));
}

function formatDirectDebitAmountEdit(value, selectionStart) {
  const raw = String(value == null ? "" : value);
  const position = Number.isInteger(selectionStart) ? Math.max(0, Math.min(selectionStart, raw.length)) : raw.length;
  if (!/^[0-9۰-۹٠-٩,٬\s]*$/.test(raw)) return { value: raw, caret: position };

  const normalized = normalizeAmountDigits(raw);
  const digits = normalized.replace(/[,٬\s]/g, "");
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const digitsBeforeCaret = normalizeAmountDigits(raw.slice(0, position)).replace(/\D/g, "").length;
  if (!digitsBeforeCaret) return { value: formatted, caret: 0 };

  let seen = 0;
  let caret = formatted.length;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) seen += 1;
    if (seen === digitsBeforeCaret) {
      caret = index + 1;
      break;
    }
  }
  return { value: formatted, caret };
}

function responseMessage(response, fallback) {
  const data = response || {};
  const errors = Array.isArray(data.errors)
    ? data.errors.map((error) => error && error.message).filter(Boolean)
    : [];
  return errors.join("\n") || data.description || fallback;
}

function readEnabledOrganizations(response) {
  if (!response || response.status !== true) {
    throw new Error(responseMessage(response, "دریافت فهرست سازمان‌ها ناموفق بود."));
  }
  const result = response.result || {};
  if (result.success !== true) {
    throw new Error(result.message || "دریافت فهرست سازمان‌ها ناموفق بود.");
  }
  if (
    !Array.isArray(result.data) ||
    !result.data.every(
      (item) =>
        item &&
        typeof item.orgId === "string" &&
        item.orgId.trim() &&
        typeof item.companyName === "string" &&
        item.companyName.trim(),
    )
  ) {
    throw new Error("فهرست سازمان‌ها معتبر نیست.");
  }
  return result.data;
}

function buildDirectDebitRequest(orgId, amountInput, organizations) {
  if (!Array.isArray(organizations) || !organizations.some((item) => item.orgId === orgId)) {
    throw new Error("سازمان انتخاب‌شده در فهرست مجاز نیست.");
  }
  const amountText = normalizeAmountDigits(amountInput == null ? "" : amountInput).replace(/[,٬\s]/g, "");
  const amount = Number(amountText);
  if (!/^\d+$/.test(amountText) || !Number.isSafeInteger(amount) || amount <= 0) {
    throw new Error("مبلغ باید عدد صحیح مثبت به ریال باشد.");
  }
  return { orgId, amount };
}

function readDirectDebitOutcome(response) {
  const result = (response && response.result) || {};
  if (response && response.status === true && result.success === true) {
    return { kind: "success", message: result.message || "عملیات با موفقیت انجام شد." };
  }
  return {
    kind: "review",
    message:
      (response && response.status === true && result.message) ||
      responseMessage(response, "نتیجه انتقال بانکی نامشخص است."),
  };
}

function createDirectDebitSession({ confirm, post }) {
  return {
    state: "idle",
    message: "",
    async submit(orgId, amountInput, organizations) {
      if (this.state !== "idle") return { kind: "blocked" };
      const payload = buildDirectDebitRequest(orgId, amountInput, organizations);
      const organization = organizations.find((item) => item.orgId === orgId);
      this.state = "confirming";
      let approved;
      try {
        approved = await confirm(organization, payload.amount);
      } catch (error) {
        this.state = "idle";
        throw error;
      }
      if (!approved) {
        this.state = "idle";
        return { kind: "cancelled" };
      }
      this.state = "submitting";
      let outcome;
      try {
        outcome = readDirectDebitOutcome(await post(payload));
      } catch (error) {
        outcome = {
          kind: "review",
          message: responseMessage(error && error.response && error.response.data, "پاسخ قطعی از سرویس دریافت نشد."),
        };
      }
      this.state = outcome.kind;
      this.message = outcome.message;
      return outcome;
    },
  };
}

function parseJalaliDate(value, label) {
  const date = moment(String(value).replace(/\//g, "-"), "jYYYY-jMM-jDD", true);
  if (!date.isValid()) throw new Error(`${label} معتبر نیست.`);
  return date;
}

function buildDirectDebitLogsRequest(filters = {}) {
  const page = filters.page === undefined ? 1 : Number(filters.page);
  const perPage = filters.perPage === undefined ? 20 : Number(filters.perPage);
  if (!Number.isInteger(page) || page < 1 || !Number.isInteger(perPage) || perPage < 1 || perPage > 100) {
    throw new Error("شماره یا اندازهٔ صفحه معتبر نیست.");
  }
  const payload = { page, perPage };
  if (filters.orgId) payload.orgId = filters.orgId;
  if (filters.userName && filters.userName.trim()) payload.userName = filters.userName.trim();
  if (filters.status) {
    if (!["Pending", "Success", "Failed", "Unknown"].includes(filters.status)) {
      throw new Error("وضعیت گزارش معتبر نیست.");
    }
    payload.status = filters.status;
  }
  const from = filters.fromDate ? parseJalaliDate(filters.fromDate, "از تاریخ") : null;
  const to = filters.toDate ? parseJalaliDate(filters.toDate, "تا تاریخ") : null;
  if (from && to && from.isAfter(to, "day")) throw new Error("بازهٔ تاریخ معتبر نیست.");
  if (from) payload.fromDate = from.format("YYYY-MM-DDT00:00:00");
  if (to) payload.toDate = to.format("YYYY-MM-DDT23:59:59");
  return payload;
}

function readDirectDebitLogs(response) {
  if (!response || response.status !== true) {
    throw new Error(responseMessage(response, "دریافت گزارش برداشت ناموفق بود."));
  }
  const result = response.result || {};
  if (!Array.isArray(result.list) || !Number.isInteger(result.totalCount) || result.totalCount < 0) {
    throw new Error("ساختار گزارش برداشت معتبر نیست.");
  }
  return { list: result.list, totalCount: result.totalCount };
}

function formatDirectDebitDate(value) {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("jYYYY/jMM/jDD HH:mm:ss") : "-";
}

module.exports = {
  formatDirectDebitAmountEdit,
  responseMessage,
  readEnabledOrganizations,
  buildDirectDebitRequest,
  readDirectDebitOutcome,
  createDirectDebitSession,
  buildDirectDebitLogsRequest,
  readDirectDebitLogs,
  formatDirectDebitDate,
};
